/**
 * Phase 5D.1 — persistent maintenance ownership on change reviews.
 * Separate from findings.maintenance (TIA classification) and Scenario assigned_to.
 * Feature 2B — priority / stale / Change Impact deep-link (no second inbox).
 */

import { z } from 'zod';

export const MAINTENANCE_ITEM_STATUSES = ['open', 'in_progress', 'resolved'] as const;
export type MaintenanceItemStatus = (typeof MAINTENANCE_ITEM_STATUSES)[number];

/** Explainable priority — thin heuristics only (Feature 2B). */
export const MAINTENANCE_ITEM_PRIORITIES = ['high', 'medium', 'low'] as const;
export type MaintenanceItemPriority = (typeof MAINTENANCE_ITEM_PRIORITIES)[number];

export const MAINTENANCE_ITEM_STALE_REASONS = ['superseded', 'target_missing'] as const;
export type MaintenanceItemStaleReason = (typeof MAINTENANCE_ITEM_STALE_REASONS)[number];

export const MaintenanceItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  changeReviewId: z.string().uuid(),
  scenarioId: z.string().uuid().nullable(),
  scenarioTitle: z.string().nullable().optional(),
  flowId: z.string().uuid().nullable(),
  flowName: z.string().nullable().optional(),
  status: z.enum(MAINTENANCE_ITEM_STATUSES),
  assigneeUserId: z.string().uuid().nullable(),
  assigneeName: z.string().nullable().optional(),
  kind: z.enum(['manual', 'automation', 'both', 'automation_first']).nullable().optional(),
  reason: z.string(),
  /** 5C.1 scenario deep link when scenarioId is set. */
  scenarioHref: z.string().nullable().optional(),
  /** Existing flow editor hash (`#/flow/:id`) when flowId is set. */
  flowHref: z.string().nullable().optional(),
  /**
   * Feature 2B — always lands on Change Impact ownership (not Scenario-only).
   * Includes reviewId + itemId (+ optional sha) for exact focus.
   */
  changeImpactHref: z.string().nullable().optional(),
  /** Feature 2B — HIGH / MEDIUM / LOW from kind + reason keywords. */
  priority: z.enum(MAINTENANCE_ITEM_PRIORITIES).optional(),
  /** Feature 2B — review superseded or target deleted. */
  stale: z.boolean().optional(),
  staleReason: z.enum(MAINTENANCE_ITEM_STALE_REASONS).nullable().optional(),
  /** Commit SHA of the owning change review (for deep-link / context). */
  commitSha: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().uuid().nullable(),
});
export type MaintenanceItem = z.infer<typeof MaintenanceItemSchema>;

/** Feature 2B — project open-work summary on Change Impact (not a second inbox). */
export const MaintenanceOwnershipQueueSchema = z.object({
  openCount: z.number().int().nonnegative(),
  unassignedCount: z.number().int().nonnegative(),
  assignedToMeCount: z.number().int().nonnegative(),
  /** Bounded open/in_progress rows across the project (newest first). */
  items: z.array(MaintenanceItemSchema).max(50),
});
export type MaintenanceOwnershipQueue = z.infer<typeof MaintenanceOwnershipQueueSchema>;

export const ListMaintenanceItemsResponseSchema = z.object({
  items: z.array(MaintenanceItemSchema),
});
export type ListMaintenanceItemsResponse = z.infer<typeof ListMaintenanceItemsResponseSchema>;

export const PatchMaintenanceItemRequestSchema = z
  .object({
    status: z.enum(MAINTENANCE_ITEM_STATUSES).optional(),
    assigneeUserId: z.string().uuid().nullable().optional(),
  })
  .refine((body) => body.status !== undefined || body.assigneeUserId !== undefined, {
    message: 'At least one of status or assigneeUserId is required',
  });
export type PatchMaintenanceItemRequest = z.infer<typeof PatchMaintenanceItemRequestSchema>;

export const MaintenanceItemResponseSchema = z.object({
  item: MaintenanceItemSchema,
});
export type MaintenanceItemResponse = z.infer<typeof MaintenanceItemResponseSchema>;
